import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Copy, Check, RefreshCw } from "lucide-react";

import { getOrCreateUploadSession, regenerateUploadSession } from "../uploadSessions.js";
import Message from "./Message.js";

// Toont een deelbare link + QR-code waarmee iemand via de telefoon foto's kan
// maken/kiezen en uploaden voor deze locatie. Handig als je op een computer zit
// zonder de foto's bij de hand.
function PhoneUploadCard({ venueId, venueName }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // Zit de gebruiker zelf al op een telefoon/tablet? Dan is de QR-code nutteloos
  // (je kunt je eigen scherm niet scannen) en richten we de kaart op het
  // doorsturen van de link naar iemand die de foto's wel heeft.
  const [isMobile] = useState(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    if (/Android|iPhone|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true;
    // iPad meldt zich op iPadOS vaak als Mac; herken 'm aan touch.
    if (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1) return true;
    return false;
  });

  useEffect(() => {
    let cancelled = false;
    if (!venueId) return undefined;
    setLoading(true);
    getOrCreateUploadSession(venueId, venueName)
      .then((res) => {
        if (!cancelled) {
          setUrl(res.url);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setError("De uploadlink kon niet worden aangemaakt.");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      setError("Kopiëren lukte niet. Selecteer en kopieer de link handmatig.");
    }
  }

  async function regenerate() {
    setRegenerating(true);
    setError("");
    try {
      const res = await regenerateUploadSession(venueId, venueName);
      setUrl(res.url);
    } catch (err) {
      console.error(err);
      setError("Er ging iets mis bij het aanmaken van een nieuwe link.");
    }
    setRegenerating(false);
  }

  return (
    <div className="rounded-sm border border-accent/30 bg-accent-subtle p-5">
      <div className="flex items-center gap-2 mb-1">
        <Smartphone size={18} className="text-accent" />
        <h4 className="text-sm font-heading font-bold text-text-primary">
          {isMobile ? "Foto's bij iemand anders?" : "Foto's op je telefoon?"}
        </h4>
      </div>
      <p className="text-sm text-text-secondary mb-4">
        {isMobile
          ? "Heb je de foto's niet op deze telefoon? Deel de link met iemand die ze wel heeft, dan kan diegene ze direct toevoegen aan deze locatie. Inloggen is niet nodig. Heb je ze zelf bij de hand? Gebruik dan gewoon het fotoveld hieronder."
          : "Scan de QR-code met je telefoon, of deel de link, om direct foto's te maken of te kiezen uit je galerij. Ze worden meteen toegevoegd aan deze locatie."}
      </p>

      {loading ? (
        <div className="flex items-center gap-3 py-4">
          <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-text-secondary">Link aanmaken...</span>
        </div>
      ) : url ? (
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          {!isMobile && (
            <div className="bg-white p-3 rounded-sm border border-border shrink-0 mx-auto sm:mx-0">
              <QRCodeSVG value={url} size={132} />
            </div>
          )}

          <div className="flex-1 w-full min-w-0">
            <label className="label-base">Deelbare uploadlink</label>
            <div className="flex gap-2">
              <input type="text" readOnly value={url} className="input-base text-sm" onFocus={(e) => e.target.select()} />
              <button
                type="button"
                onClick={copyLink}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 rounded-sm border border-border bg-bg-surface text-sm font-medium text-text-primary hover:bg-bg-sunken transition-colors duration-fast"
              >
                {copied ? <Check size={15} className="text-success" /> : <Copy size={15} />}
                {copied ? "Gekopieerd" : "Kopieer"}
              </button>
            </div>

            <p className="text-xs text-text-tertiary mt-3 leading-relaxed">
              {isMobile
                ? "De link is 30 dagen geldig."
                : "Heeft een collega de foto's? Stuur de link (of straks de herinneringsmail) gewoon door, inloggen is niet nodig. De link is 30 dagen geldig."}
            </p>

            <button
              type="button"
              onClick={regenerate}
              disabled={regenerating}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-text-secondary hover:text-accent transition-colors duration-fast disabled:opacity-50"
            >
              <RefreshCw size={13} className={regenerating ? "animate-spin" : ""} /> Nieuwe link maken
            </button>
          </div>
        </div>
      ) : null}

      {error && (
        <div className="mt-3">
          <Message text={error} />
        </div>
      )}
    </div>
  );
}

export default PhoneUploadCard;
